import { createContext, useCallback, useContext, useEffect, useState, ReactNode } from 'react';
import { authApi, type Membership, type AuthUser, type TenantId } from '../api';
import { getToken, setToken, setActiveTenantId } from '../api/client';

type AuthState = {
  loading: boolean;
  user: AuthUser | null;
  memberships: Membership[];
  sessionTenantId: TenantId | null;
  refresh: () => Promise<void>;
  loginWithToken: (token: string, tenantId: TenantId | null) => Promise<void>;
  logout: () => Promise<void>;
};

const AuthCtx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [sessionTenantId, setSessionTenantId] = useState<TenantId | null>(null);

  const clearLocal = () => {
    setToken(null);
    setActiveTenantId(null);
    setUser(null);
    setMemberships([]);
    setSessionTenantId(null);
  };

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      if (!getToken()) {
        setUser(null);
        setMemberships([]);
        setSessionTenantId(null);
        return;
      }
      const r = await authApi.me();
      setUser(r.user);
      setMemberships(r.memberships);
      setSessionTenantId(r.session_tenant_id);
      if (r.session_tenant_id) setActiveTenantId(r.session_tenant_id);
    } catch {
      clearLocal();
    } finally {
      setLoading(false);
    }
  }, []);

  const loginWithToken = useCallback(async (token: string, tenantId: TenantId | null) => {
    setToken(token);
    if (tenantId != null) setActiveTenantId(tenantId);
    await refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    await authApi.logout().catch(() => null);
    clearLocal();
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    const onLogoutEvent = () => { clearLocal(); };
    window.addEventListener('litigate:logout', onLogoutEvent);
    return () => window.removeEventListener('litigate:logout', onLogoutEvent);
  }, []);

  return (
    <AuthCtx.Provider value={{ loading, user, memberships, sessionTenantId, refresh, loginWithToken, logout }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
